import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { AdminService } from '../admin.service';
import { CoreApiService, WriteResult } from '../../core-api.service';
import { NotificationService } from '../../notification.service';
import { ProjectsService } from '../../projects/projects.service';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import { Role } from '../../shared/models/role.model';

@Injectable({
  providedIn: 'root',
})
export class RolesService {

  constructor(
    private adminService: AdminService,
    private coreApi: CoreApiService,
    private notifications: NotificationService,
    private projectsService: ProjectsService,
  ) { }

  getRoles({ refresh = false } = {}): Observable<Role[]> {
    return this.projectsService.getRoles({ refresh });
  }

  createRole(
    label: string,
    actions: PermissionAction[],
  ): Observable<Role> {
    return this.coreApi.roles.post({ label, actions }).pipe(
      tap(() => {
        this.notifications.success('roles.create.success');
        this.getRoles({ refresh: true });
      }),
      catchError(error => {
        this.notifications.error('roles.create.error');
        throw error;
      }),
    );
  }

  updateRole(
    role: Role,
    rolePermissions: { [key: string]: boolean }
  ): Observable<WriteResult> {
    return this.coreApi.roles.put(role._id, {
      label: role.label,
      actions: Object.keys(rolePermissions)
        .map(v => rolePermissions[v] ? v : null)
        .filter(v => v) as PermissionAction[],
    }).pipe(
      tap(() => {
        this.notifications.success('roles.update.success');
        this.getRoles({ refresh: true });
      }),
      catchError(error => {
        this.notifications.error('roles.update.error');
        throw error;
      }),
    );
  }

  removeRole(roleId: string): Observable<WriteResult> {
    return this.coreApi.roles.delete(roleId).pipe(
      tap(() => {
        this.notifications.success('roles.delete.success');
        this.getRoles({ refresh: true });
      }),
      catchError(error => {
        this.notifications.error('roles.delete.error');
        throw error;
      }),
    );
  }

  updateRoleGroups(
    roleId: string,
    postGroupIds: string[],
    deleteGroupIds: string[],
  ): Observable<WriteResult[]> {
    if (postGroupIds.length + deleteGroupIds.length === 0) {
      return of([]);
    }
    return forkJoin([
      ...postGroupIds.map(groupId => this.coreApi.groups.roles.post(groupId, { _id: roleId })),
      ...deleteGroupIds.map(groupId => this.coreApi.groups.roles.delete(groupId, roleId)),
    ]).pipe(
      tap(() => {
        this.notifications.success('roles.groupRoles.update.success');
        this.adminService.getAllGroups({ refresh: true });
        this.adminService.getAllProjects({ refresh: true });
        this.projectsService.getGroups({ refresh: true }); // user groups
        this.projectsService.getProjects({ refresh: true }); // user projects
      }),
      catchError(error => {
        this.notifications.error('roles.groupRoles.update.error');
        throw error;
      }),
    )
  }

}
